import request, { HttpMethod } from '@app/libs/request';
import config from '@app/config';
import { ExportedSpreadsheetDTO } from '@app/domain/userManagement/organizations';
import { OrganizationalUser, OrganizationalUserServerResponse } from '@app/domain/userManagement/organizationUsers';
import { ORGANIZATION_HEADER_NAME } from '@app/utils/userIdentityHeadersGenerator';
import { UpdateWebAppRolesOfUsersRequest, UpdateWebUserRolesRequest } from '@app/domain/userManagement/users';
import { MAX_CHANGES_PER_SAVE } from '@app/domain/uiConsts';

export async function getAllUsersOfOrganizationWithNsknoxUsers(
  organizationId: string,
): Promise<OrganizationalUserServerResponse[]> {
  return request<OrganizationalUserServerResponse[]>(config.serverUrls.backee, `/api/organizations/:organizationId/users`, {
    method: HttpMethod.get,
    pathParams: { organizationId },
    errorsHandler: {
      default: {
        message: 'Cannot get users of organization.',
      },
    },
  });
}

export async function getAllUsersOfOrganization(organizationId: string): Promise<OrganizationalUserServerResponse[]> {
  return request<OrganizationalUserServerResponse[]>(config.serverUrls.backee, `/api/organizations/users`, {
    method: HttpMethod.get,
    headers: {
      [ORGANIZATION_HEADER_NAME]: organizationId,
    },
    errorsHandler: {
      default: {
        message: 'Cannot get users of organization.',
      },
    },
  });
}

export async function addUserToOrganization(
  organizationId: string,
  tenantUserId: string,
): Promise<OrganizationalUserServerResponse> {
  return request<OrganizationalUserServerResponse>(config.serverUrls.backee, `/api/organizations/:organizationId/users`, {
    data: {
      tenantUserId,
    },
    method: HttpMethod.post,
    pathParams: { organizationId },
    errorsHandler: {
      default: {
        message: 'Cannot add user to organization.',
      },
    },
  });
}

export async function exportOrganizationUserRoles(organizationId: string): Promise<ExportedSpreadsheetDTO> {
  return request<ExportedSpreadsheetDTO>(config.serverUrls.backee, `/api/organizations/:organizationId/users/export`, {
    method: HttpMethod.get,
    pathParams: { organizationId },
    errorsHandler: {
      default: {
        message: 'Cannot download file',
      },
    },
  });
}

export async function exportCurrentOrganizationUserRoles(organizationId: string): Promise<ExportedSpreadsheetDTO> {
  return request<ExportedSpreadsheetDTO>(config.serverUrls.backee, `/api/organizations/users/export`, {
    method: HttpMethod.get,
    headers: {
      [ORGANIZATION_HEADER_NAME]: organizationId,
    },
    errorsHandler: {
      default: {
        message: 'Cannot download file',
      },
    },
  });
}

export async function suspendUserFromOrganization(organizationId: string, tenantUserId: string): Promise<boolean> {
  return request<boolean>(config.serverUrls.backee, `/api/organizations/:organizationId/suspended-users`, {
    data: {
      tenantUserId,
    },
    method: HttpMethod.post,
    pathParams: { organizationId },
    errorsHandler: {
      default: {
        message: 'Cannot suspend user from organization.',
      },
    },
  });
}

export async function killUserSessions(tenantUserId: string): Promise<null> {
  return request<null>(config.serverUrls.backee, `/api/users/${tenantUserId}/web-sessions`, {
    method: HttpMethod.delete,
    errorsHandler: {
      default: {
        message: "We couldn't complete this action, try again later.",
      },
    },
  });
}

export async function updateUserRoles(
  organizationId: string,
  tenantUserId: string,
  data: UpdateWebUserRolesRequest,
): Promise<OrganizationalUserServerResponse> {
  return request<OrganizationalUserServerResponse>(
    config.serverUrls.backee,
    `/api/organizations/:organizationId/users/:tenantUserId/web-app-roles`,
    {
      data,
      method: HttpMethod.patch,
      pathParams: { organizationId, tenantUserId },
      errorsHandler: {
        USER_NOT_IN_ORGANIZATION_ERROR: {
          message: (): string => `The selected user in not in the organization.`,
        },
        default: {
          message: 'Cannot update the roles of the user.',
        },
      },
    },
  );
}

// A failed batch leaves the earlier batches applied, so its message says how many users were already updated
export async function updateRolesOfUsers(
  organizationId: string,
  organizationalUsers: OrganizationalUser[],
  change: UpdateWebUserRolesRequest,
  alreadySavedCount: number,
  onBatchSaved: (savedTenantUserIds: string[], updatedUsers: OrganizationalUserServerResponse[]) => void,
): Promise<void> {
  const tenantUserIdToNames = Object.fromEntries(
    organizationalUsers.map((organizationalUser) => [organizationalUser.tenantUserId, organizationalUser.name]),
  );
  const totalCount = alreadySavedCount + organizationalUsers.length;

  for (let batchStart = 0; batchStart < organizationalUsers.length; batchStart += MAX_CHANGES_PER_SAVE) {
    const batchTenantUserIds = organizationalUsers
      .slice(batchStart, batchStart + MAX_CHANGES_PER_SAVE)
      .map((organizationalUser) => organizationalUser.tenantUserId);
    const data: UpdateWebAppRolesOfUsersRequest = { tenantUserIds: batchTenantUserIds, ...change };
    const savedCount = alreadySavedCount + batchStart;
    const savedSoFar = savedCount
      ? `${savedCount} of the ${totalCount} users were already updated, and the rest were not.`
      : 'Nothing was changed.';

    const updatedUsers = await request<OrganizationalUserServerResponse[]>(
      config.serverUrls.backee,
      `/api/organizations/:organizationId/users/web-app-roles`,
      {
        data,
        method: HttpMethod.patch,
        pathParams: { organizationId },
        errorsHandler: {
          USERS_NOT_IN_ORGANIZATION_ERROR: {
            message: ({ tenantUserIds }): string =>
              `Cannot update the roles: these users are no longer active in the organization: ${tenantUserIds
                .map((tenantUserId) => tenantUserIdToNames[tenantUserId] ?? tenantUserId)
                .join(', ')}. ${savedSoFar}`,
          },
          CHANGE_IN_PROGRESS_ERROR: {
            message: (): string => `Someone else is changing this organization right now. ${savedSoFar} Save again in a moment.`,
          },
          default: {
            // A knoxer may have applied the change before another failed, so this can't promise nothing changed
            message: savedCount
              ? `Cannot update the roles of all the users: ${savedCount} of the ${totalCount} were updated. Save again to update the rest.`
              : 'Cannot update the roles of the users. Reload and try again.',
          },
        },
      },
    );
    onBatchSaved(batchTenantUserIds, updatedUsers);
  }
}
